import dayjs from 'dayjs';

import { DictTypes } from '@/constants';
import { generateRandomId } from '@/services';
import {
  formatPhoneNumber,
  formatPrice,
  onlyDigits
} from '@/services/formatter';
import { safeKey } from '@/services/helpers/dict';
import { formatNumber } from '@/services/numberFormatter';
import type { TDict } from '@/types/dicts';

import type {
  TIeAdditionalInfoCreateDto,
  TIeClientContactsForm,
  TIeClientInfoForm,
  TIeClientJobInfoForm,
  TIeFilesCreateDto,
  TIeFirstStepForm,
  TIeForm,
  TIeLeasingCreateDto,
  TIePersonalInfoCreateDto,
  TIeProductCreateDto,
  TIeSecondStepForm,
  TIeSubject,
  TIeSubjectCreateDto,
  TIeThirdStepForm,
  TLeasingFile,
  TLeasingOrderDetailed,
  TLeasingSubject
} from '../types';

const toString = (value: unknown): string => {
  return String(value ?? '');
};

const toNumber = (value: unknown): number => {
  const result = formatNumber(value);
  if (!result.isValid) throw new Error('Invalid number');
  return result.rawValue;
};

const toFile = (value: unknown): { base64: string; name: string } => {
  if (typeof value !== 'object' || value === null)
    throw new Error('Invalid file');
  if ('base64' in value && 'name' in value) {
    return {
      base64: toString(value.base64),
      name: toString(value.name)
    };
  }
  throw new Error('Invalid file');
};

const tryToSet = (set: () => void) => {
  try {
    set();
  } catch (error) {
    console.error(error);
  }
};

type TProps = {
  request: TLeasingOrderDetailed;
  files: TLeasingFile[];
  dicts: TDict;
};

export const toForm = (props?: TProps): TIeForm => {
  return {
    ...toFirstStepForm(props),
    ...toSecondStepForm(props),
    ...toThirdStepForm(props)
  };
};

const toFirstStepForm = (props?: TProps): TIeFirstStepForm => {
  if (!props)
    return {
      autoType: '',
      dealer: '',
      initialPayment: '',
      loanTerm: '',
      paymentScheme: '',
      proofInCome: false,
      subjects: [createIeSubject()]
    };

  const { request, dicts } = props;

  const dealerCode: string = toString(
    dicts[DictTypes.Dealers]?.getBy.uuid[safeKey(request.dealerUuid)]?.code
  );

  const { applicationData, leasingSubjects } = request;
  const subjects =
    leasingSubjects.length > 0
      ? leasingSubjects.map(createIeSubject)
      : [createIeSubject()];

  const { product } = applicationData ?? {};

  return {
    autoType: toString(product?.autoType),
    dealer: dealerCode,
    initialPayment: formatPrice(request.initialPayment),
    loanTerm: toString(product?.loanTerm),
    paymentScheme: toString(product?.paymentScheme),
    proofInCome: product?.proofInCome ?? false,
    subjects
  };
};

const toSecondStepForm = (props?: TProps): TIeSecondStepForm => {
  return {
    ...toClientInfoForm(props),
    ...toClientJobInfoForm(props),
    ...toClientContactsForm(props)
  };
};

const toClientInfoForm = (props?: TProps): TIeClientInfoForm => {
  if (!props)
    return {
      birthDate: '',
      childrenCount: '',
      documentType: '',
      gender: '',
      marriageStatus: ''
    };

  const {
    request: { applicationData, client }
  } = props;
  const { personalInfo, additionalInfo } = applicationData ?? {};

  const birthDate: string = personalInfo?.dateBirth ?? client?.birthDate ?? '';

  const childrenCount: string = toString(
    additionalInfo?.children ?? client?.childCount ?? ''
  );

  const documentType: string = toString(
    personalInfo?.docType ?? client?.documentType?.code ?? ''
  );

  const gender: string = toString(
    personalInfo?.sex ?? client?.gender.code ?? ''
  );

  const marriageStatus: string = toString(
    personalInfo?.familyStatus ?? client?.maritalStatus?.code ?? ''
  );

  return {
    birthDate,
    childrenCount,
    documentType,
    gender,
    marriageStatus
  };
};

const toClientJobInfoForm = (props?: TProps): TIeClientJobInfoForm => {
  if (!props)
    return {
      additionalSalary: '',
      companyName: '',
      jobAddress: '',
      jobTitle: '',
      salary: '',
      socialStatus: '',
      workPhoneNumber: ''
    };

  const { request } = props;
  const { applicationData, client } = request;
  const { additionalInfo, personalInfo } = applicationData ?? {};

  const additionalSalary: string = formatPrice(
    additionalInfo?.additionalSalary ?? client?.additionalIncome ?? ''
  );

  const companyName: string =
    additionalInfo?.companyName ?? request?.companyName ?? '';

  const jobAddress: string =
    personalInfo?.jobAddress ?? client?.workAddress ?? '';

  const jobTitle: string = additionalInfo?.jobTitle ?? client?.jobTitle ?? '';

  const salary: string = formatPrice(
    additionalInfo?.salary ?? client?.salary ?? ''
  );

  const socialStatus: string = toString(
    additionalInfo?.socialStatus ?? client?.socialStatus?.code ?? ''
  );

  const workPhoneNumber: string = formatPhoneNumber(
    additionalInfo?.workPhoneNumber ?? client?.workTelNumber ?? ''
  );

  return {
    additionalSalary,
    companyName,
    jobAddress,
    jobTitle,
    salary,
    socialStatus,
    workPhoneNumber
  };
};

const toClientContactsForm = (props?: TProps): TIeClientContactsForm => {
  if (!props)
    return {
      contactName: '',
      contactPhone: '',
      contactType: ''
    };

  const {
    request: { applicationData, client }
  } = props;
  const { additionalInfo } = applicationData ?? {};

  const contactName: string =
    additionalInfo?.addContactsFullName ?? client?.addContact?.fullName ?? '';

  const contactPhone: string = formatPhoneNumber(
    additionalInfo?.addContactsTelNum ?? client?.addContact?.phoneNumber ?? ''
  );

  const contactType: string = toString(
    additionalInfo?.addContactsIsRelated ??
      client?.addContact?.addContactsIsRelated?.code ??
      ''
  );

  return {
    contactName,
    contactPhone,
    contactType
  };
};

const toThirdStepForm = (props?: TProps): TIeThirdStepForm => {
  if (!props)
    return {
      clientPhoto: null,
      docPhoto: null,
      docScanSocialStatus: null,
      incomeCert: null,
      noticeActivity: null,
      otherDoc: null
    };

  const result: TIeThirdStepForm = {
    clientPhoto: null,
    docPhoto: null,
    docScanSocialStatus: null,
    incomeCert: null,
    noticeActivity: null,
    otherDoc: null
  };

  props.files.forEach((file) => {
    switch (file.type) {
      case 'clientBiometricPhoto':
      case 'clientPhoto':
        result.clientPhoto ??= file;
        break;
      case 'docPhoto':
        result.docPhoto ??= file;
        break;
      case 'docScanSocialStatus':
        result.docScanSocialStatus ??= file;
        break;
      case 'incomeCert':
        result.incomeCert ??= file;
        break;
      case 'noticeActivity':
        result.noticeActivity ??= file;
        break;
      case 'otherDoc':
        result.otherDoc ??= file;
        break;
    }
  });

  return result;
};

export const toCreateDto = (
  data: TIeForm,
  request: TLeasingOrderDetailed
): TIeLeasingCreateDto => {
  return {
    additionalInfo: toAdditionalInfoCreateDto(data),
    personalInfo: toPersonalInfoCreateDto(data),
    product: toProductsCreateDto(data),
    uuid: request.uuid
  };
};

const toAdditionalInfoCreateDto = (
  data: TIeForm
): TIeAdditionalInfoCreateDto => {
  return {
    addContactsFullName: data.contactName,
    addContactsIsRelated: toNumber(data.contactType),
    addContactsTelNum: onlyDigits(data.contactPhone),
    additionalSalary: onlyDigits(data.additionalSalary),
    children: onlyDigits(data.childrenCount),
    companyName: data.companyName,
    files: toFilesCreateDto(data),
    jobTitle: data.jobTitle,
    salary: onlyDigits(data.salary),
    socialStatus: toNumber(data.socialStatus),
    workTelNumber: onlyDigits(data.workPhoneNumber)
  };
};

const toPersonalInfoCreateDto = (data: TIeForm): TIePersonalInfoCreateDto => {
  const parsedBirthDate = dayjs(data.birthDate);
  if (!parsedBirthDate.isValid()) {
    throw new Error('Invalid birth date');
  }

  return {
    dateBirth: parsedBirthDate.format('YYYY-MM-DD'),
    docType: toNumber(data.documentType),
    familyStatus: toNumber(data.marriageStatus),
    jobAddress: data.jobAddress,
    sex: toNumber(data.gender)
  };
};

const toProductsCreateDto = (data: TIeForm): TIeProductCreateDto => {
  return {
    autoType: toNumber(data.autoType),
    initFee: toNumber(data.initialPayment),
    loanTerm: toNumber(data.loanTerm),
    paymentScheme: toNumber(data.paymentScheme),
    subjects: data.subjects.map(toSubjectCreateDto)
  };
};

const toFilesCreateDto = (data: TIeForm): TIeFilesCreateDto => {
  const dto: TIeFilesCreateDto = {
    noticeActivity: toFile(data.noticeActivity)
  };

  tryToSet(() => (dto.clientPhoto = toFile(data.clientPhoto)));
  tryToSet(() => (dto.docPhoto = toFile(data.docPhoto)));
  tryToSet(() => (dto.docScanSocialStatus = toFile(data.docScanSocialStatus)));
  tryToSet(() => (dto.incomeCert = toFile(data.incomeCert)));
  tryToSet(() => (dto.otherDoc = toFile(data.otherDoc)));

  return dto;
};

const toSubjectCreateDto = (data: TIeSubject): TIeSubjectCreateDto => {
  return {
    brand: toNumber(data.brandCode),
    cost: toNumber(data.cost),
    engineType: toNumber(data.engineType),
    equipment: data.equipmentUuid,
    model: toNumber(data.modelCode),
    quantity: toNumber(data.count),
    year: toNumber(data.year)
  };
};

export const createIeSubject = (subject?: TLeasingSubject): TIeSubject => {
  if (!subject)
    return {
      brandCode: '',
      brandName: '',
      cost: '',
      count: '1',
      engineType: '',
      equipmentName: '',
      equipmentUuid: '',
      id: generateRandomId('leasing_subject'),
      modelCode: '',
      modelName: '',
      year: ''
    };

  return {
    brandCode: toString(subject.brand.code),
    brandName: subject.brand.name,
    cost: formatPrice(subject.cost),
    count: toString(subject.quantity),
    engineType: toString(subject.engineType.code),
    equipmentName: subject.equipmentName,
    equipmentUuid: subject.equipmentUuid,
    id: subject.uuid,
    modelCode: toString(subject.model.code),
    modelName: subject.model.name,
    year: toString(subject.year)
  };
};
